import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { Check, X, Trash2, ArrowLeft, Star } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BASE = process.env.REACT_APP_BACKEND_URL;
function resolveUrl(u) { return (!u || u.startsWith('http')) ? u : `${BASE}${u}`; }

function StarDisplay({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(n => (
        <Star key={n} size={12} className={n <= rating ? 'fill-[#C6A978] text-[#C6A978]' : 'text-[#DDD7CE]'} />
      ))}
    </div>
  );
}

export default function AdminReviewsPage() {
  const [reviews, setReviews] = useState([]);
  const [filter, setFilter] = useState('all'); // all | approved | pending
  const [loading, setLoading] = useState(true);

  const fetchReviews = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API}/admin/reviews`;
      if (filter === 'approved') url += '?approved=true';
      else if (filter === 'pending') url += '?approved=false';
      const { data } = await axios.get(url, { withCredentials: true });
      setReviews(data);
    } catch (err) {
      console.error('Failed to fetch reviews', err);
    } finally {
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => { fetchReviews(); }, [fetchReviews]);

  const approve = async (id) => {
    try {
      await axios.put(`${API}/admin/reviews/${id}/approve`, {}, { withCredentials: true });
      fetchReviews();
    } catch (err) { console.error(err); }
  };

  const reject = async (id) => {
    try {
      await axios.put(`${API}/admin/reviews/${id}/reject`, {}, { withCredentials: true });
      fetchReviews();
    } catch (err) { console.error(err); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this review permanently?')) return;
    try {
      await axios.delete(`${API}/admin/reviews/${id}`, { withCredentials: true });
      fetchReviews();
    } catch (err) { console.error(err); }
  };

  return (
    <main data-testid="admin-reviews-page" className="pt-20 min-h-screen bg-[#F6F3EE]">
      <div className="max-w-6xl mx-auto px-4 md:px-8 py-8">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div>
            <Link to="/admin" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.2em] text-[#7A7369] hover:text-[#1E1C1A] mb-2">
              <ArrowLeft size={12} /> Back to Dashboard
            </Link>
            <p className="text-[11px] font-medium uppercase tracking-[0.2em] text-[#C6A978] mb-1">Admin Panel</p>
            <h1 className="font-['Cormorant_Garamond'] text-2xl md:text-3xl font-light tracking-tighter text-[#1E1C1A]">
              Review Moderation
            </h1>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap mb-6">
          {[
            { key: 'all', label: 'All' },
            { key: 'approved', label: 'Approved' },
            { key: 'pending', label: 'Pending' },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              className={`px-4 py-2 text-xs tracking-[0.1em] uppercase border transition-colors ${
                filter === f.key
                  ? 'bg-[#1E1C1A] text-[#F6F3EE] border-[#1E1C1A]'
                  : 'bg-transparent text-[#7A7369] border-[#DDD7CE] hover:border-[#1E1C1A] hover:text-[#1E1C1A]'
              }`}
            >
              {f.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="text-center py-20">
            <div className="w-6 h-6 border border-[#C6A978] border-t-transparent rounded-full animate-spin mx-auto" />
          </div>
        ) : reviews.length === 0 ? (
          <div className="bg-[#E8E1D8]/50 border border-[#DDD7CE] py-16 text-center text-sm font-light text-[#7A7369]">
            No reviews found
          </div>
        ) : (
          <div className="space-y-3">
            {reviews.map(r => (
              <div key={r.id} className="bg-[#E8E1D8]/50 border border-[#DDD7CE] p-4 md:p-5 flex gap-4">
                {r.image_url && (
                  <img
                    src={resolveUrl(r.image_url)}
                    alt=""
                    className="w-16 h-16 md:w-20 md:h-20 object-cover flex-shrink-0 cursor-pointer"
                    onClick={() => window.open(resolveUrl(r.image_url), '_blank')}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-3 mb-1.5 flex-wrap">
                    <div>
                      <p className="font-['Cormorant_Garamond'] text-base font-medium text-[#1E1C1A]">{r.customer_name}</p>
                      <div className="flex items-center gap-2 mt-0.5">
                        <StarDisplay rating={r.rating} />
                        <span className="text-[10px] text-[#7A7369]">{new Date(r.created_at).toLocaleDateString('en-IN')}</span>
                        {r.product_id ? (
                          <span className="text-[10px] text-[#7A7369]">· Product: {r.product_id.slice(0, 8)}…</span>
                        ) : (
                          <span className="text-[10px] text-[#C6A978]">· General</span>
                        )}
                      </div>
                    </div>
                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 ${
                      r.approved ? 'bg-emerald-50 text-emerald-700' : 'bg-amber-50 text-amber-700'
                    }`}>
                      {r.approved ? 'Approved' : 'Pending'}
                    </span>
                  </div>
                  <p className="text-xs md:text-sm font-light text-[#3A3632] leading-relaxed mb-3 whitespace-pre-wrap">{r.comment}</p>
                  <div className="flex gap-2 flex-wrap">
                    {!r.approved && (
                      <button
                        onClick={() => approve(r.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-wider uppercase border border-emerald-600 text-emerald-700 hover:bg-emerald-600 hover:text-white transition-colors"
                      >
                        <Check size={11} /> Approve
                      </button>
                    )}
                    {r.approved && (
                      <button
                        onClick={() => reject(r.id)}
                        className="flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-wider uppercase border border-amber-600 text-amber-700 hover:bg-amber-600 hover:text-white transition-colors"
                      >
                        <X size={11} /> Hide
                      </button>
                    )}
                    <button
                      onClick={() => remove(r.id)}
                      className="flex items-center gap-1 px-3 py-1.5 text-[10px] tracking-wider uppercase border border-red-600 text-red-700 hover:bg-red-600 hover:text-white transition-colors"
                    >
                      <Trash2 size={11} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </main>
  );
}